import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import AdminLayout from "@/components/Layout";
import HlsPreviewPlayer from "@/components/admin/HlsPreviewPlayer";
import {
  Wrench, Download, FileText, RadioTower, ExternalLink,
  Copy, Check, Loader2, Route,
} from "lucide-react";

/**
 * /admin/tools — operator utilities:
 *   1. M3U export (download all enabled channels w/ stream_url) + live stats
 *   2. Stream preview player (HLS.js with mixed-content awareness)
 *   3. "VLC ile Aç" handoff (single-channel m3u download for desktop player)
 */
export default function Tools() {
  const [stats, setStats] = useState(null);
  const [statsBusy, setStatsBusy] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [previewUrl, setPreviewUrl] = useState("");
  const [activeUrl,  setActiveUrl]  = useState("");
  const [copied, setCopied] = useState(false);

  const loadStats = async () => {
    setStatsBusy(true);
    try {
      const r = await api.get("/tools/export-m3u/stats");
      setStats(r.data);
    } catch {
      toast.error("İstatistik yüklenemedi.");
    } finally { setStatsBusy(false); }
  };

  useEffect(() => { loadStats(); }, []);

  const downloadM3U = async (scheme = null) => {
    setDownloading(true);
    try {
      // Use raw fetch so we get the binary blob & honour the Content-Disposition filename.
      const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const qs = scheme ? `?scheme=${scheme}` : "";
      const res = await fetch(`${baseUrl}/api/tools/export-m3u${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `lumen-channels-${Date.now()}.m3u`;
      const count = res.headers.get("X-Channel-Count") || "?";

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);

      toast.success(`${count} kanal indirildi → ${filename}`);
      loadStats();
    } catch (e) {
      toast.error(`İndirme başarısız: ${e.message}`);
    } finally { setDownloading(false); }
  };

  const openInVLC = () => {
    if (!previewUrl) return toast.error("Önce bir stream URL girin.");
    // Generate a single-channel m3u file the user can open with VLC
    const safe = previewUrl.replace(/[^\w\-./:?=&]/g, "");
    const content = `#EXTM3U\n#EXTINF:-1,Önizleme\n${previewUrl}\n`;
    const blob = new Blob([content], { type: "application/x-mpegurl" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lumen-preview-${Date.now()}.m3u`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Önizleme.m3u indirildi — VLC ile açın");
    void safe; // suppress lint
  };

  const openPassthrough = () => {
    if (!previewUrl) return toast.error("Once bir stream URL girin.");
    const sep = previewUrl.includes("?") ? "&" : "?";
    const direct = `${previewUrl}${sep}passthrough=true`;
    window.open(direct, "_blank", "noopener,noreferrer");
  };

  const copyUrl = async () => {
    if (!previewUrl) return;
    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kopyalanamadı.");
    }
  };

  const startPreview = () => {
    if (!previewUrl.trim()) return toast.error("Stream URL girin.");
    setActiveUrl(previewUrl.trim());
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <Wrench className="text-amber-500" size={24} />
          <h1 className="text-2xl font-bold text-white" data-testid="tools-page-title">Araçlar</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Operatör yardımcıları — kanal listesi dışa aktarımı, stream URL test/önizleme.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ---- M3U Export Card ---- */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 grid place-items-center">
                <FileText className="text-amber-400" size={18} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">M3U Listesi</h2>
                <p className="text-xs text-gray-500">Tüm aktif kanalları standart M3U formatında indir.</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <Stat label="Toplam"     value={stats?.total_channels} loading={statsBusy} />
              <Stat label="İndirilebilir" value={stats?.exportable}     loading={statsBusy} accent="amber" />
              <Stat label="URL eksik"  value={stats?.skipped_no_url}  loading={statsBusy} accent="red" />
            </div>

            {stats && stats.exportable === 0 && (
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs text-amber-300/90 mb-4" data-testid="tools-empty-warning">
                Kanal kataloğunda <span className="font-mono">stream_url</span> tanımlı kanal yok. M3U dosyası boş olur.
                Kanallar sayfasından M3U içe aktarımı yapabilirsiniz.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                onClick={() => downloadM3U(null)}
                disabled={downloading || (stats && stats.exportable === 0)}
                className="bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
                data-testid="tools-download-m3u-btn"
              >
                {downloading
                  ? <><Loader2 size={18} className="animate-spin" /> İndiriliyor…</>
                  : <><Download size={18} /> M3U İndir (HTTP · VLC)</>}
              </button>
              <button
                onClick={() => downloadM3U("https")}
                disabled={downloading || (stats && stats.exportable === 0)}
                className="bg-slate-800 hover:bg-slate-700 border border-amber-500/30 disabled:opacity-40 text-amber-300 font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
                data-testid="tools-download-m3u-https-btn"
                title="Tarayıcıda hls.js önizleme için HTTPS varyantı"
              >
                <Download size={18} /> HTTPS Varyantı
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 text-center">
              <strong>HTTP varyantı VLC için varsayılan</strong> (sertifika hatasız).
              Tarayıcı içi mixed-content engelinden kaçınmak için HTTPS varyantını kullanın.
            </p>

            <div className="mt-4 p-3 rounded-lg border border-slate-800 bg-slate-950/60 text-[11px] font-mono text-gray-500 break-all">
              <span className="text-gray-600">GET</span>{" "}
              <span className="text-amber-400">/api/tools/export-m3u</span>
              <br />
              <span className="text-gray-600">Auth: Bearer &lt;admin-token&gt;</span>
            </div>
          </div>

          {/* ---- Stream Preview Card ---- */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 grid place-items-center">
                <RadioTower className="text-amber-400" size={18} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Yayın Önizleme</h2>
                <p className="text-xs text-gray-500">HLS.js ile test oynatma. HTTP kaynaklar için VLC handoff.</p>
              </div>
            </div>

            <label className="text-xs font-bold text-gray-500 uppercase ml-1">Stream URL</label>
            <div className="flex gap-2 mt-1.5 mb-3">
              <input
                value={previewUrl}
                onChange={(e) => setPreviewUrl(e.target.value)}
                placeholder="https://example.com/playlist.m3u8"
                className="flex-1 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-sm"
                data-testid="tools-preview-url"
                onKeyDown={(e) => { if (e.key === "Enter") startPreview(); }}
              />
              <button
                onClick={copyUrl}
                disabled={!previewUrl}
                className="px-3 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-white rounded-xl transition flex items-center justify-center"
                title="URL'yi kopyala"
                type="button"
                data-testid="tools-preview-copy"
              >
                {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <button
                onClick={startPreview}
                disabled={!previewUrl}
                className="bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25 disabled:opacity-40 font-bold py-2.5 rounded-xl transition flex items-center justify-center gap-2 text-sm"
                data-testid="tools-preview-start"
                type="button"
              >
                Tarayıcıda Yükle
              </button>
              <button
                onClick={openInVLC}
                disabled={!previewUrl}
                className="bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-white font-bold py-2.5 rounded-xl transition flex items-center justify-center gap-2 text-sm"
                data-testid="tools-preview-vlc"
                type="button"
              >
                <ExternalLink size={14} /> VLC ile Aç
              </button>
              <button
                onClick={openPassthrough}
                disabled={!previewUrl}
                className="bg-transparent border border-red-500 text-red-400 hover:bg-red-500/10 disabled:opacity-40 font-bold py-2.5 rounded-xl transition flex items-center justify-center gap-2 text-sm"
                data-testid="tools-preview-passthrough"
                type="button"
              >
                <Route size={14} /> Direkt (Passthrough)
              </button>
            </div>

            <HlsPreviewPlayer url={activeUrl} />

            <details className="mt-4 group">
              <summary className="text-[11px] text-gray-500 hover:text-amber-400 cursor-pointer select-none">
                ℹ️ Mixed content (HTTP/HTTPS) sorunu hakkında
              </summary>
              <div className="mt-2 p-3 rounded-lg bg-slate-950/60 border border-slate-800 text-[11px] text-gray-500 leading-relaxed">
                Admin paneli HTTPS üzerinde çalışır. Tarayıcılar güvenlik gereği HTTPS sayfa içinden
                <span className="font-mono text-amber-400"> http:// </span> medya kaynaklarına erişimi engeller.
                Çözümler:
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  <li><strong>VLC ile Aç</strong> — masaüstü oynatıcı bu kısıtlamadan etkilenmez</li>
                  <li>Upstream'i HTTPS'e taşı (Cloudflare/Let's Encrypt)</li>
                  <li>Edge node'da TLS terminasyonu — node, HTTP upstream'i HTTPS olarak tekrar yayınlar</li>
                </ul>
              </div>
            </details>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, loading, accent }) {
  const colorMap = {
    amber: "text-amber-400",
    red:   "text-red-400",
    default: "text-white",
  };
  return (
    <div className="bg-slate-950 border border-slate-800 rounded-xl p-3 text-center">
      <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${colorMap[accent] || colorMap.default}`}>
        {loading ? <Loader2 size={18} className="animate-spin inline-block text-gray-500" /> : (value ?? 0)}
      </div>
    </div>
  );
}
